import { useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, googleProvider, db } from '../config/firebase';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userStatus, setUserStatus] = useState(null);
  const [isApproved, setIsApproved] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);

        // Get custom claims from ID token
        const tokenResult = await authUser.getIdTokenResult();
        const claims = tokenResult.claims;

        setIsApproved(claims.approved === true);
        setIsAdmin(claims.admin === true);

        // Set up real-time listener for user status in Firestore
        const userDocRef = doc(db, 'users', authUser.uid);

        // First check if document exists
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          // Create pending user document on first sign-in
          await setDoc(userDocRef, {
            email: authUser.email,
            displayName: authUser.displayName || authUser.email,
            photoURL: authUser.photoURL || null,
            status: 'pending',
            requestedAt: Date.now(),
          });
          setUserStatus('pending');
        } else {
          setUserStatus(userDoc.data().status);
        }

        // Listen for status changes
        const unsubscribeStatus = onSnapshot(userDocRef, (doc) => {
          if (doc.exists()) {
            setUserStatus(doc.data().status);
          }
        });

        setLoading(false);

        // Return cleanup for status listener
        return () => unsubscribeStatus();
      } else {
        setUser(null);
        setUserStatus(null);
        setIsApproved(false);
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserStatus(null);
      setIsApproved(false);
      setIsAdmin(false);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  // Force refresh of ID token to get updated custom claims
  const refreshClaims = async () => {
    if (user) {
      const tokenResult = await user.getIdTokenResult(true); // true = force refresh
      const claims = tokenResult.claims;
      setIsApproved(claims.approved === true);
      setIsAdmin(claims.admin === true);
    }
  };

  return {
    user,
    loading,
    userStatus,
    isApproved,
    isAdmin,
    signIn,
    signOut,
    refreshClaims,
  };
}
